/**
 * Radiology test service — study types with facility pricing.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';

export interface RadiologyTestWithPrice {
  id: number;
  test_name: string;
  modality: string;
  price: number;
}

export interface RadiologyTestNameOption {
  id: number | null;
  test_code: string | null;
  test_name: string;
  modality: string | null;
  is_active: boolean | null;
}

interface RadiologyTestNamesResponse {
  success?: boolean;
  count?: number;
  data?: RadiologyTestNameOption[];
  error?: string;
}

interface RadiologyTestApiModality {
  id?: number;
  name?: string;
  description?: string;
}

interface RadiologyTestApiItem {
  radiology_test_id?: number;
  radiology_test_id_fk?: number;
  test_name: string;
  modality?: string | RadiologyTestApiModality;
  price?: number | string;
}

interface RadiologyTestsWithPricesResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: RadiologyTestApiItem[];
  pagination?: {
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
    currentPage?: number;
    totalRecords?: number;
  };
}

function parsePrice(value: number | string | undefined): number {
  if (value == null || value === '') {
    return 0;
  }
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
}

function getModalityName(modality: RadiologyTestApiItem['modality']): string {
  if (typeof modality === 'string') {
    return modality.trim();
  }
  if (modality && typeof modality === 'object' && modality.name) {
    return String(modality.name).trim();
  }
  return '';
}

export function normalizeRadiologyTest(item: RadiologyTestApiItem): RadiologyTestWithPrice | null {
  const id = item.radiology_test_id ?? item.radiology_test_id_fk;
  const testName = item.test_name?.trim();

  if (id == null || !testName) {
    return null;
  }

  return {
    id,
    test_name: testName,
    modality: getModalityName(item.modality),
    price: parsePrice(item.price),
  };
}

export async function getRadiologyTestsWithPrices(
  facilityId: string | number,
  options?: { page?: number; limit?: number; modality_id?: number }
): Promise<{ success: boolean; data?: RadiologyTestWithPrice[]; error?: string }> {
  try {
    const url = API_ENDPOINTS.RADIOLOGY_TESTS.WITH_PRICES({
      facility_id: facilityId,
      page: options?.page ?? 1,
      limit: options?.limit ?? 200,
      modality_id: options?.modality_id,
    });
    const response = await apiGet<RadiologyTestsWithPricesResponse>(url, { requireAuth: true });
    const payload = response.data;

    if (payload?.success === false) {
      return { success: false, error: payload.message || 'Failed to load radiology tests' };
    }

    if (Array.isArray(payload?.data)) {
      const data = payload.data
        .map(normalizeRadiologyTest)
        .filter((test): test is RadiologyTestWithPrice => test != null);
      return { success: true, data };
    }

    return { success: false, error: 'Invalid radiology tests response' };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load radiology tests',
    };
  }
}

export async function getRadiologyTestNames(
  facilityId: string | number,
  options?: { modality_id?: number; active_only?: boolean }
): Promise<{ success: boolean; data?: RadiologyTestNameOption[]; error?: string }> {
  try {
    const url = API_ENDPOINTS.RADIOLOGY_TESTS.TEST_NAMES({
      facility_id: facilityId,
      modality_id: options?.modality_id,
      active_only: options?.active_only ?? true,
    });
    const response = await apiGet<RadiologyTestNamesResponse>(url, { requireAuth: true });
    const payload = response.data;

    if (payload?.success === false) {
      return { success: false, error: payload.error || 'Failed to load radiology test names' };
    }

    if (Array.isArray(payload?.data)) {
      return { success: true, data: payload.data };
    }

    return { success: false, error: 'Invalid radiology test names response' };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load radiology test names',
    };
  }
}
